// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { fontConfig } from "@vanmeter/XDL/constants";
import IconPaginationArrowRight from "@vanmeter/XDL/Icons/IconPaginationArrowRight";
import { CategoryPropertiesTypes } from "@vanmeter/XDL/types";
import { ellipsis, xParserOptions } from "@vanmeter/XDL/Utilities";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {}

export interface ShippingMessageBoxStyles {
    wrapper?: InjectableCss;
    imageStyle?: LazyImageProps;
    titleText?: TypographyPresentationProps;
    contentText?: TypographyPresentationProps;
    moreLink?: LinkPresentationProps;
}

const styles: ShippingMessageBoxStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 12px;
        `,
    },
    imageStyle: {
        width: "300px",
        css: css`
            margin-bottom: 12px;
            &,
            img {
                aspect-ratio: 300 / 225;
            }
        `,
    },
    titleText: {
        variant: "h5",
    },
    contentText: {
        ...fontConfig.small,
        css: css`
            > p {
                font-size: ${fontConfig.small.size}px;
                line-height: ${fontConfig.small.lineHeight};
                ${ellipsis(8)}
            }
            &:empty {
                display: none;
            }
        `,
    },
    moreLink: {
        mergeCss: true,
        icon: { iconProps: { src: IconPaginationArrowRight, size: 16 }, position: "right" },
        css: css`
            width: fit-content;
        `,
    },
};

const MenuCategoryInfo: FC<OwnProps & CategoryPropertiesTypes> = ({
    contentBody,
    contentButtonLink,
    contentButtonText,
    contentHeading,
    contentImage,
}) => {
    const history = useHistory();

    return (
        <StyledWrapper {...styles.wrapper}>
            {contentImage && <LazyImage {...styles.imageStyle} src={contentImage} altText={contentHeading} />}
            {contentHeading && <Typography {...styles.titleText}>{contentHeading}</Typography>}
            {contentBody && (
                <Typography {...styles.contentText}>
                    {parse(transformContentHtml(contentBody || ""), xParserOptions)}
                </Typography>
            )}
            {contentButtonLink && (
                <Link
                    {...styles.moreLink}
                    onClick={() => {
                        history.push(contentButtonLink);
                    }}
                    // XNG-Changes
                    aria-label={contentButtonText}
                >
                    {contentButtonText}
                </Link>
            )}
        </StyledWrapper>
    );
};

export default MenuCategoryInfo;
